import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { 
  Bell, User, GraduationCap, Shield, CreditCard, 
  Settings, LogOut, Store, Users, MessageSquare, Megaphone, CheckCheck, PalmtreeIcon
} from 'lucide-react';
import GlobalSearchBar from './GlobalSearchBar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { toast } from 'sonner';

const TopBar = () => {
  const { user, logout, apiCall } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOnHoliday, setIsOnHoliday] = useState(false);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'super_admin';

  // Holiday mode — vendors only
  useEffect(() => {
    if (isAdmin || !user?.id) return;
    apiCall('/api/vendors/me/holiday-mode')
      .then(r => setIsOnHoliday(r?.is_on_holiday || false))
      .catch(() => {});
  }, [user?.id, isAdmin]);

  const toggleHolidayMode = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHolidayLoading(true);
    try {
      const res = await apiCall('/api/vendors/me/holiday-mode', {
        method: 'POST',
        body: JSON.stringify({ is_on_holiday: !isOnHoliday }),
      });
      setIsOnHoliday(res.is_on_holiday);
      toast(res.is_on_holiday ? 'Holiday Mode On — store paused' : 'Store is Active again', {
        description: res.message,
      });
    } catch {
      toast.error('Could not update holiday mode');
    } finally {
      setHolidayLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || 'U';
  };

  const fetchNotifications = useCallback(async () => {
    try {
      // silentAuth: true — background polling must NOT trigger logout on token expiry
      if (isAdmin) {
        const [feedRes, countRes] = await Promise.all([
          apiCall('/api/admin/notifications/feed?limit=5', { silentAuth: true }),
          apiCall('/api/admin/notifications/unread-count', { silentAuth: true })
        ]);
        setNotifications(feedRes?.notifications || []);
        setUnreadCount(countRes?.unread_count || 0);
      } else {
        const countRes = await apiCall('/api/vendor-notifications/unread-count', { silentAuth: true });
        setUnreadCount(countRes?.unread_count || 0);
        const feedRes = await apiCall('/api/vendor-notifications?limit=5', { silentAuth: true });
        setNotifications(feedRes?.notifications || []);
      }
    } catch {
      // Silently ignore notification fetch errors
    }
  }, [apiCall, isAdmin]);

  useEffect(() => {
    // iter-251 perf — Pause polling when tab is backgrounded. Without
    // this, a backgrounded admin tab continued firing 2 parallel
    // notification calls every 30s (= 4 calls/min) indefinitely. The
    // visibilitychange listener forces a single refresh the moment
    // the tab returns to foreground so the bell catches up immediately.
    // Interval relaxed from 30s → 60s; matches OrderNotification.jsx
    // cadence to avoid overlapping polls.
    const tick = () => {
      if (!document.hidden) fetchNotifications();
    };
    fetchNotifications(); // initial fetch on mount
    const interval = setInterval(tick, 60000); // 60s (was 30s)
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [fetchNotifications]);

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      if (isAdmin) {
        await apiCall('/api/admin/notifications/mark-all-read', { method: 'POST' });
      } else {
        await apiCall('/api/vendor-notifications/read-all', { method: 'POST' });
      }
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const getNotifColor = (type) => {
    const colors = {
      new_vendor: 'bg-blue-500',
      listing_approval: 'bg-amber-500',
      vendor_status_change: 'bg-green-500',
      high_value_order: 'bg-purple-500',
      system_alert: 'bg-red-500',
      lead: 'bg-blue-500',
      order: 'bg-green-500',
      booking_request: 'bg-indigo-500',
      compliance_upgrade: 'bg-teal-500',
      payout: 'bg-emerald-500',
    };
    return colors[type] || 'bg-gray-400';
  };

  const getNotifRoute = (notif) => {
    const d = notif.data || {};
    const type = notif.type;

    // Admin notification routes
    if (isAdmin) {
      switch (type) {
        case 'listing_approval': {
          if (!d.listing_id) return '/admin/approvals';
          const cat = (d.listing_type || 'product').toLowerCase().replace(/s$/, '');
          return `/edit-listing/${cat}/${d.listing_id}`;
        }
        case 'new_vendor':
          return d.vendor_id ? `/vendors/${d.vendor_id}` : '/vendors';
        case 'vendor_status_change':
          return d.vendor_id ? `/vendors/${d.vendor_id}` : '/vendors';
        case 'high_value_order':
        case 'order':
          return d.order_id ? `/orders/${d.order_id}` : '/orders';
        case 'lead':
          return d.lead_id ? `/leads/${d.lead_id}` : '/leads';
        case 'payout':
          return '/admin/payouts';
        case 'compliance_upgrade':
        case 'compliance':
          return d.vendor_id ? `/vendors/${d.vendor_id}` : '/admin/vendor-compliance';
        case 'booking_request':
          return d.booking_id ? `/bookings/${d.booking_id}` : '/bookings';
        case 'subscription':
          return '/admin/subscription-plans';
        case 'campaign':
          return '/admin/campaigns';
        case 'system_alert':
          return '/admin/notifications';
        default:
          return '/admin/notifications';
      }
    }

    // Vendor notification routes
    switch (type) {
      case 'listing_approval': {
        if (!d.listing_id) return '/vendor/listings';
        const cat = (d.listing_type || 'product').toLowerCase().replace(/s$/, '');
        return `/edit-listing/${cat}/${d.listing_id}`;
      }
      case 'order':
        return d.order_id ? `/orders/${d.order_id}` : '/orders';
      case 'lead':
        return d.lead_id ? `/vendor/leads/${d.lead_id}` : '/vendor/leads';
      case 'payout':
        return '/vendor/payouts';
      case 'subscription':
        return '/vendor/billing';
      case 'booking_request':
        return d.booking_id ? `/vendor/bookings/${d.booking_id}` : '/vendor/bookings';
      case 'compliance':
      case 'compliance_upgrade':
        return '/vendor/compliance';
      default:
        return '/vendor/notifications';
    }
  };

  const handleNotifClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        if (isAdmin) {
          await apiCall(`/api/admin/notifications/${notif.id}/read`, { method: 'POST' });
        } else {
          await apiCall(`/api/vendor-notifications/${notif.id}/read`, { method: 'POST' });
        }
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch { /* ignore */ }
    }
    // Navigate
    const route = getNotifRoute(notif);
    navigate(route);
  };

  return (
    <div className="h-16 bg-white border-b border-gray-200 fixed top-0 right-0 left-64 z-10 flex items-center justify-between px-8">
      {/* Global Search Bar with Autocomplete */}
      <GlobalSearchBar 
        className="flex-1 max-w-xl" 
        placeholder="Search orders, products, customers..."
      />

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Feedback & Updates */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(isAdmin ? '/admin/feedback' : '/feedback')}
          className="relative"
          title="Feedback & Updates"
          data-testid="feedback-topbar-btn"
        >
          <Megaphone className="h-5 w-5" />
        </Button>

        {/* Notifications Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notifications-bell">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="flex items-center justify-between px-3 py-2">
              <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
              {unreadCount > 0 && (
                <button 
                  onClick={handleMarkAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <CheckCheck className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No notifications yet
                </div>
              ) : (
                notifications.map((notif, idx) => (
                  <React.Fragment key={notif.id || idx}>
                    {idx > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuItem 
                      className="flex flex-col items-start p-3 cursor-pointer"
                      onClick={() => handleNotifClick(notif)}
                      data-testid={`notification-item-${idx}`}
                    >
                      <div className="flex items-start gap-2 w-full">
                        <div className={`h-2 w-2 ${notif.read ? 'bg-gray-300' : getNotifColor(notif.type)} rounded-full mt-2 flex-shrink-0`}></div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${notif.read ? 'text-gray-600' : 'font-medium text-gray-900'}`}>{notif.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </React.Fragment>
                ))
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="justify-center text-blue-600 font-medium cursor-pointer"
              onClick={() => navigate(isAdmin ? '/admin/notifications' : '/vendor/notifications')}
            >
              View All Notifications
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-gray-900 text-white text-xs">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {user?.role === 'super_admin' || user?.admin_type === 'super_admin' 
                    ? 'Super Admin' 
                    : user?.role?.replace('_', ' ')}
                </p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(isAdmin ? '/admin/training' : '/vendor/training')}>
                <GraduationCap className="mr-2 h-4 w-4" />
                Training Academy
              </DropdownMenuItem>
            </DropdownMenuGroup>
            
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-gray-400">Settings</DropdownMenuLabel>
            
            {isAdmin ? (
              /* Admin-specific menu items */
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/admin/security')}>
                  <Shield className="mr-2 h-4 w-4" />
                  Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/admin/notifications')}>
                  <Bell className="mr-2 h-4 w-4" />
                  Notifications
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/admin/team')}>
                  <Users className="mr-2 h-4 w-4" />
                  Team & Roles
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Platform Settings
                </DropdownMenuItem>
              </DropdownMenuGroup>
            ) : (
              /* Vendor-specific menu items */
              <DropdownMenuGroup>
                {/* Holiday Mode toggle — right at the top of vendor menu */}
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="flex items-center justify-between cursor-default focus:bg-transparent"
                  data-testid="holiday-mode-toggle-item"
                >
                  <span className="flex items-center gap-2 text-sm">
                    <span className="text-base">🏖️</span>
                    Holiday Mode
                    {isOnHoliday && (
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">ON</span>
                    )}
                  </span>
                  <Switch
                    checked={isOnHoliday}
                    onCheckedChange={() => toggleHolidayMode({ preventDefault: ()=>{}, stopPropagation: ()=>{} })}
                    disabled={holidayLoading}
                    className="ml-3 scale-90"
                    data-testid="holiday-mode-switch"
                  />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/vendor/store-profile')}>
                  <Store className="mr-2 h-4 w-4" />
                  Store Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/vendor/security')}>
                  <Shield className="mr-2 h-4 w-4" />
                  Security
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/vendor/billing')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/vendor/notifications')}>
                  <Bell className="mr-2 h-4 w-4" />
                  Notifications
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings/team')}>
                  <Users className="mr-2 h-4 w-4" />
                  Team & Roles
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/messaging')}>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Messages
                </DropdownMenuItem>
              </DropdownMenuGroup>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TopBar;
